import React, { useEffect } from 'react';
import { Modal, Form, Input, Switch, Button, Drawer } from 'antd';
import { useTranslation } from 'react-i18next';
import { SuppliersApi } from 'api/api';
import { SupplierDto, CreateSupplierCommand, UpdateSupplierCommand } from 'api/models';
import openNotificationWithIcon from 'utility/notification';
import { useMediaQuery } from 'react-responsive';

interface SupplierFormProps {
  visible: boolean;
  editMode: boolean;
  supplier: SupplierDto | null;
  onCancel: () => void;
  onSuccess: () => Promise<void>;
}

const suppliersApi = new SuppliersApi();

export default function SupplierForm({ visible, editMode, supplier, onCancel, onSuccess }: SupplierFormProps) {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 767 });

  useEffect(() => {
    if (visible && editMode && supplier) {
      form.setFieldsValue({
        name: supplier.name,
        description: supplier.description,
        email: supplier.email,
        phoneNumber: supplier.phoneNumber,
        address: supplier.address,
        isActive: supplier.isActive,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        isActive: true,
      });
    }
  }, [visible, editMode, supplier, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editMode && supplier) {
        const command: UpdateSupplierCommand = {
          id: supplier.id!,
          name: values.name,
          description: values.description,
          email: values.email,
          phoneNumber: values.phoneNumber,
          address: values.address,
          isActive: values.isActive,
        };
        await suppliersApi.apiSuppliersIdPut({ id: supplier.id!, updateSupplierCommand: command });
        openNotificationWithIcon('success', t('suppliers:updated', 'Supplier updated successfully'));
      } else {
        const command: CreateSupplierCommand = {
          name: values.name,
          description: values.description,
          email: values.email,
          phoneNumber: values.phoneNumber,
          address: values.address,
        };
        await suppliersApi.apiSuppliersPost({ createSupplierCommand: command });
        openNotificationWithIcon('success', t('suppliers:created', 'Supplier created successfully'));
      }
      
      await onSuccess();
      onCancel();
    } catch (err) {
      const message = editMode 
        ? t('suppliers:update-error', 'Failed to update supplier')
        : t('suppliers:create-error', 'Failed to create supplier');
      openNotificationWithIcon('error', message);
    }
  };

  const formContent = (
    <Form
      form={form}
      layout="vertical"
      name="supplierForm"
      style={{ padding: isMobile ? '0px' : '16px' }}
    >
      <Form.Item
        name="name"
        label={t('suppliers:name', 'Name')}
        rules={[{ required: true, message: t('suppliers:name-required', 'Please enter supplier name') }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="description"
        label={t('suppliers:description', 'Description')}
      >
        <Input.TextArea rows={3} />
      </Form.Item>

      <Form.Item
        name="email"
        label={t('suppliers:email', 'Email')}
        rules={[
          { type: 'email', message: t('suppliers:email-invalid', 'Please enter a valid email') }
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="phoneNumber"
        label={t('suppliers:phone', 'Phone Number')}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="address"
        label={t('suppliers:address', 'Address')}
      >
        <Input.TextArea rows={2} />
      </Form.Item>

      <Form.Item
        name="isActive"
        label={t('suppliers:active', 'Active')}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
    </Form>
  );

  // Use Drawer on mobile instead of Modal
  if (isMobile) {
    return (
      <Drawer
        title={
          editMode 
            ? t('suppliers:edit-title', 'Edit Supplier') 
            : t('suppliers:create-title', 'Create Supplier')
        }
        placement="right"
        width="100%"
        onClose={onCancel}
        open={visible}
        headerStyle={{ padding: '16px' }}
        bodyStyle={{ padding: '16px' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button onClick={onCancel}>
              {t('global:cancel', 'Cancel')}
            </Button>
            <Button type="primary" onClick={handleSubmit}>
              {editMode ? t('global:save', 'Save') : t('global:create', 'Create')}
            </Button>
          </div>
        }
        footerStyle={{ padding: '12px 16px' }}
      >
        {formContent}
      </Drawer>
    );
  }

  // Use Modal on desktop
  return (
    <Modal
      title={
        editMode 
          ? t('suppliers:edit-title', 'Edit Supplier') 
          : t('suppliers:create-title', 'Create Supplier')
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      width={600}
    >
      {formContent}
    </Modal>
  );
}